import { type FC, memo } from "react";

import { LinkButton } from "../../../../../common/components/Link";
import { useDispatch } from "react-redux";
import { clearCart } from "~/features/cart/cart/redux/actions/cartPageActions";
import DeleteCartConfirmationModal from "~/domains/cart/components/DeleteCartConfirmationModal";
import { useTranslation } from "react-i18next";
import { openModal } from "~/common/utils/ModalService";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSessionState } from "~/app/providers/SessionProvider";
import { userIsLoggedIn, userIsSalesman } from "~/domains/user/utils/roles";
import { activeCartResourceId } from "~/features/cart/cart/utils/cartResourceIdentity";

interface ClearCartButtonProps {
  className?: string;
  isB2BCart?: boolean;
}

export const useClearCartAction = () => {
  const dispatch = useDispatch();
  const session = useSessionState();
  const cartId = clearCartResourceId(session);

  return () => {
    if ((session.cartActionsDisabled && !session.duplicateRecoveryActiveCart) || (!cartId && !userIsSalesman())) {
      return;
    }
    openModal("deleteCartConfirmation", (close) => (
      <DeleteCartConfirmationModal
        onOk={() => {
          close();
          dispatch(clearCart(cartId));
        }}
        onCancel={close}
      />
    ));
  };
};

const ClearCartButton: FC<ClearCartButtonProps> = ({ className, isB2BCart = false }) => {
  const { t } = useTranslation();
  const session = useSessionState();

  const clickHandler = useClearCartAction();

  return (
    <LinkButton
      onClick={clickHandler}
      disabled={session.cartActionsDisabled && !session.duplicateRecoveryActiveCart}
      data-test-id={"btn-clear-cart"}
      className={className}
      iconBefore={isB2BCart ? <DeleteOutlineOutlinedIcon /> : <DeleteIcon />}
    >
      {t(isB2BCart ? "page.cart.action.clearCart" : "page.cart.button.clearCart")}
    </LinkButton>
  );
};

function clearCartResourceId(session: ReturnType<typeof useSessionState>): string | undefined {
  const activeCart = session.cartActionsDisabled ? session.duplicateRecoveryActiveCart : session.data?.activeCart;
  return activeCartResourceId(activeCart, userIsLoggedIn());
}

export default memo(ClearCartButton);
